/* eslint-disable no-underscore-dangle */
/* eslint-disable react/prop-types */
import React from "react";
import { useSelector } from "react-redux";
import { Typography, makeStyles } from "@material-ui/core";
import { Chart, LinearScale, Tooltip } from "chart.js-next";
import {
  SankeyController,
  Flow,
} from "lib/chartjs-chart-sankey/chartjs-chart-sankey.esm";
import CustomTooltip from "app/common/chart/CustomTooltipComponent";
import _ from "lodash";
import { nFormatter } from "lib/utils";

Chart.register(LinearScale, SankeyController, Flow, Tooltip);

const useStyles = makeStyles((theme) => ({
  chartContainer: {
    position: "relative",
    overflow: "hidden",
    flexGrow: "1",
    [theme.breakpoints.up("xs")]: {
      height: "155px",
    },
    [theme.breakpoints.up("md")]: {
      height: "255px",
    },
    [theme.breakpoints.up("lg")]: {
      height: "100%",
    },
  },
  chartCanvas: {
    [theme.breakpoints.up("xs")]: {
      maxHeight: "120px",
    },
    [theme.breakpoints.up("sm")]: {
      maxHeight: "150px",
    },
    [theme.breakpoints.up("md")]: {
      maxHeight: "240px",
    },
    [theme.breakpoints.up("lg")]: {
      maxHeight: "100%",
    },
  },
}));

function ElasticitySankeyChartComponent({ data, priceChangeDirection }) {
  const chartContainer = React.useRef(null);
  const classes = useStyles();
  const [chartItem, setChartItem] = React.useState(null);
  // eslint-disable-next-line no-unused-vars
  const catalogSlice = useSelector((state) => state.catalog);
  const [tooltipProps, setTooltipProps] = React.useState({});
  const setPositionAndData = (payload) => {
    setTooltipProps({
      top: payload.top,
      left: payload.left,
      position: "fixed",
      tooltipModel: payload.tooltipModel,
      showTooltip: true,
    });
  };

  const hide = () => {
    setTooltipProps({
      ...tooltipProps,
      showTooltip: false,
    });
  };

  const getTopElasticityTuples = (
    elasticityData,
    limit,
    source,
    color,
    isCompetitors
  ) => {
    const result = [];
    let newTuple;

    const totalSumForElasticityData = _.sumBy(
      elasticityData,
      `sum_constant_${priceChangeDirection.key}`
    );

    let otherTuplesSum = totalSumForElasticityData;

    for (let i = 0; i < limit; i += 1) {
      if (elasticityData[i]) {
        newTuple = {
          from: source,
          to: isCompetitors
            ? catalogSlice.competitors[elasticityData[i].product_id]?.label
            : catalogSlice.products[elasticityData[i].product_id]?.label,
          flow: Math.abs(
            elasticityData[i][`sum_constant_${priceChangeDirection.key}`] /
              data.aggregatedClientOwnElasticityData
          ),
          absolute:
            elasticityData[i][`sum_constant_${priceChangeDirection.key}`],
          colorFrom: color,
          colorTo: color,
        };

        result.push(newTuple);

        otherTuplesSum -=
          elasticityData[i][`sum_constant_${priceChangeDirection.key}`];
      }
    }

    if (otherTuplesSum !== 0) {
      result.push({
        from: source,
        to: isCompetitors ? `Others (Comp.)` : `Others (Cannib.)`,
        flow: Math.abs(otherTuplesSum / data.aggregatedClientOwnElasticityData),
        absolute: otherTuplesSum,
        colorFrom: color,
        colorTo: color,
      });
    }

    return result;
  };

  const chartOptions = {
    layout: {
      padding: {
        left: 0,
        right: 20,
      },
    },
    plugins: {
      tooltip: {
        mode: "index",
        intersect: true,
        enabled: false,
        custom: React.useCallback((chartModel) => {
          if (chartModel.tooltip.opacity === 0) {
            hide();
            return;
          }

          const canvas = chartContainer.current;

          if (canvas) {
            const position = canvas.getBoundingClientRect();

            // set position of tooltip
            const left =
              chartModel.tooltip.caretX <= chartModel.chart.width / 2
                ? position.left + chartModel.tooltip.caretX + 40
                : position.left + chartModel.tooltip.caretX - 220;
            const top = position.top + chartModel.tooltip.caretY;

            setPositionAndData({
              top,
              left: left > 30 ? left : 30,
              tooltipModel: chartModel,
            });
          }
        }, []),
      },
    },
  };

  React.useEffect(() => {
    if (chartItem) chartItem.destroy();
    const sumClientsCrossElasticityDataGroupedByProduct = _(
      data.clientCrossElasticityData
    )
      .groupBy("product_id")
      .map((product, id) => ({
        product_id: id,
        sum_constant_decrease: _.sumBy(product, "sum_constant_decrease"),
        sum_constant_increase: _.sumBy(product, "sum_constant_increase"),
      }))
      .value();

    const orderedClientCrossElasticityData = _.orderBy(
      sumClientsCrossElasticityDataGroupedByProduct,
      (tuple) => Math.abs(tuple[`sum_constant_${priceChangeDirection.key}`]),
      "desc"
    );

    const sumCompetitorsCrossElasticityDataGroupedByProduct = _(
      data.competitorsCrossElasticityData
    )
      .groupBy("product_id")
      .map((product, id) => ({
        product_id: id,
        sum_constant_decrease: _.sumBy(product, "sum_constant_decrease"),
        sum_constant_increase: _.sumBy(product, "sum_constant_increase"),
      }))
      .value();

    const orderedCompetitorsCrossElasticityData = _.orderBy(
      sumCompetitorsCrossElasticityDataGroupedByProduct,
      (tuple) => Math.abs(tuple[`sum_constant_${priceChangeDirection.key}`]),
      "desc"
    );

    let chartData;

    const chartColors = {
      gain: "#B2155A",
      neutral: "#e2e2e2",
      competitors: "#FFC84A",
      cannibalization: "#10A2B1",
    };

    if (priceChangeDirection.key === "decrease") {
      chartData = [
        {
          from: "Incremental",
          to: "Net Gain",
          flow: Math.abs(
            data.aggregatedCompetitorsAndAdditionalEffect /
              data.aggregatedClientOwnElasticityData
          ),
          absolute: data.aggregatedCompetitorsAndAdditionalEffect,
          colorFrom: chartColors.gain,
          colorTo: chartColors.neutral,
        },
        {
          from: "Incremental",
          to: "Total Loss",
          flow: Math.abs(
            data.aggregatedCannibalizationEffect /
              data.aggregatedClientOwnElasticityData
          ),
          absolute: data.aggregatedCannibalizationEffect,
          colorFrom: chartColors.gain,
          colorTo: chartColors.neutral,
        },
        {
          from: "Net Gain",
          to: "Additional Gain",
          flow: Math.abs(
            data.aggregatedAdditionalNet /
              data.aggregatedClientOwnElasticityData
          ),
          absolute: data.aggregatedAdditionalNet,
          colorFrom: chartColors.neutral,
          colorTo: chartColors.gain,
        },
        {
          from: "Additional Gain",
          flow: 0,
          absolute: 0,
          colorFrom: chartColors.gain,
          colorTo: chartColors.gain,
        },
        {
          from: "Net Gain",
          to: "Competitive Steal",
          flow: Math.abs(
            data.aggregatedCompetitorsCrossElasticityData /
              data.aggregatedClientOwnElasticityData
          ),
          absolute: data.aggregatedCompetitorsCrossElasticityData,
          colorFrom: chartColors.neutral,
          colorTo: chartColors.competitors,
        },
        {
          from: "Total Loss",
          to: "Cannibalization Loss",
          flow: Math.abs(
            data.aggregatedClientCrossElasticityData /
              data.aggregatedClientOwnElasticityData
          ),
          absolute: data.aggregatedClientCrossElasticityData,
          colorFrom: chartColors.neutral,
          colorTo: chartColors.cannibalization,
        },
        ...getTopElasticityTuples(
          orderedClientCrossElasticityData,
          3,
          "Cannibalization Loss",
          chartColors.cannibalization,
          false
        ),
        ...getTopElasticityTuples(
          orderedCompetitorsCrossElasticityData,
          3,
          "Competitive Steal",
          chartColors.competitors,
          true
        ),
      ];
    } else {
      chartData = [
        {
          from: "Incremental",
          to: "Total Gain",
          flow: Math.abs(
            data.aggregatedCannibalizationEffect /
              data.aggregatedClientOwnElasticityData
          ),
          absolute: data.aggregatedCannibalizationEffect,
          colorFrom: chartColors.gain,
          colorTo: chartColors.neutral,
        },
        {
          from: "Incremental",
          to: "Net Loss",
          flow: Math.abs(
            data.aggregatedCompetitorsAndAdditionalEffect /
              data.aggregatedClientOwnElasticityData
          ),
          absolute: data.aggregatedCompetitorsAndAdditionalEffect,
          colorFrom: chartColors.gain,
          colorTo: chartColors.neutral,
        },
        {
          from: "Net Loss",
          to: "Additional Loss",
          flow: Math.abs(
            data.aggregatedAdditionalNet /
              data.aggregatedClientOwnElasticityData
          ),
          absolute: -data.aggregatedAdditionalNet,
          colorFrom: chartColors.neutral,
          colorTo: chartColors.gain,
        },
        {
          from: "Additional Loss",
          flow: 0,
          absolute: 0,
          colorFrom: chartColors.gain,
          colorTo: chartColors.gain,
        },
        {
          from: "Net Loss",
          to: "Competitive Loss",
          flow: Math.abs(
            data.aggregatedCompetitorsCrossElasticityData /
              data.aggregatedClientOwnElasticityData
          ),
          absolute: data.aggregatedCompetitorsCrossElasticityData,
          colorFrom: chartColors.neutral,
          colorTo: chartColors.competitors,
        },
        {
          from: "Total Gain",
          to: "Cannibalization Steal",
          flow: Math.abs(
            data.aggregatedClientCrossElasticityData /
              data.aggregatedClientOwnElasticityData
          ),
          absolute: data.aggregatedClientCrossElasticityData,
          colorFrom: chartColors.neutral,
          colorTo: chartColors.cannibalization,
        },
        ...getTopElasticityTuples(
          orderedClientCrossElasticityData,
          3,
          "Cannibalization Steal",
          chartColors.cannibalization,
          false
        ),
        ...getTopElasticityTuples(
          orderedCompetitorsCrossElasticityData,
          3,
          "Competitive Loss",
          chartColors.competitors,
          true
        ),
      ];
    }

    if (chartContainer && chartContainer.current) {
      // eslint-disable-next-line no-new
      const chart = new Chart(chartContainer.current, {
        type: "sankey",
        data: {
          datasets: [
            {
              data: chartData,
              colorFrom: (c) => c.dataset.data[c.dataIndex].colorFrom,
              colorTo: (c) => c.dataset.data[c.dataIndex].colorTo,
            },
          ],
        },
        options: chartOptions,
      });
      setChartItem(chart);
    }
  }, [chartContainer, data]);

  return (
    <>
      {tooltipProps.showTooltip ? (
        <CustomTooltip
          tooltipProps={tooltipProps}
          items={_.map(
            tooltipProps.tooltipModel.tooltip.dataPoints[0].dataPoint._custom
              .from.to,
            (item, index) => {
              return {
                content: (
                  <div key={item.key}>
                    <Typography variant="body2">{`${item.key}`}</Typography>
                    <Typography variant="caption">
                      {`${nFormatter(item.node.absolute)}`}
                    </Typography>
                  </div>
                ),
                borderStyle: {
                  borderColor: item.node.color,
                },
                containerStyle: {
                  marginTop:
                    tooltipProps.tooltipModel.tooltip.dataPoints[0].dataPoint
                      ._custom.from.key === "Incremental" && index === 0
                      ? "0px"
                      : "10px",
                },
              };
            }
          )}
          tooltipData={{
            title:
              tooltipProps.tooltipModel.tooltip.dataPoints[0].dataPoint._custom
                .from.key === "Incremental"
                ? null
                : tooltipProps.tooltipModel.tooltip.dataPoints[0].dataPoint
                    ._custom.from.key,
          }}
        />
      ) : null}
      <canvas
        id="myChart"
        className={classes.chartCanvas}
        ref={chartContainer}
      />
    </>
  );
}

export default ElasticitySankeyChartComponent;
